import { useEffect, useState } from "react";
import { History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BulkReverseDialog } from "./BulkReverseDialog";
import {
  fmtBatchDate,
  type BatchSummary,
  type ReverseResponse,
  type ReverseResultRow,
} from "./bulkGrantTypes";

export function BulkGrantHistoryPanel() {
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [target, setTarget] = useState<BatchSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<{
    batchId: string;
    attempted: number;
    succeeded: number;
    skipped: number;
    failed: number;
    rows: ReverseResultRow[];
  } | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke(
      "admin-grant-credits",
      { body: { op: "list_batches", limit: 25 } },
    );
    setLoading(false);
    if (error) {
      toast.error(`Could not load batches: ${error.message}`);
      return;
    }
    const payload = data as { batches?: BatchSummary[]; error?: string };
    if (payload?.error) {
      toast.error(payload.error);
      return;
    }
    setBatches(payload.batches ?? []);
  };

  useEffect(() => {
    void load();
  }, []);

  const runReverse = async (reason: string) => {
    if (!target) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-grant-credits",
        {
          body: {
            op: "bulk_reverse",
            batch_id: target.batch_id,
            reason,
          },
        },
      );
      if (error) throw new Error(error.message);
      const payload = data as ReverseResponse;
      if (payload?.error) throw new Error(payload.error);

      const succeeded = payload.succeeded ?? 0;
      const skipped = payload.skipped_already_reversed ?? 0;
      const failed = payload.failed ?? 0;
      setLastResult({
        batchId: payload.batch_id ?? target.batch_id,
        attempted: payload.attempted ?? 0,
        succeeded,
        skipped,
        failed,
        rows: payload.results ?? [],
      });

      toast.success(
        `Reversed ${succeeded} grant(s)` +
          (skipped > 0 ? ` · ${skipped} already reversed` : "") +
          (failed > 0 ? ` · ${failed} failed` : ""),
      );
      if (failed > 0) {
        console.warn(
          "Bulk reverse: per-user failures",
          (payload.results ?? []).filter((r) => !r.ok && r.skipped !== "already_reversed"),
        );
      }
      setTarget(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bulk reverse failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" /> Bulk grant history
        </CardTitle>
        <CardDescription>
          Recent bulk credit grants. Reversing a batch undoes every successful
          grant in it; users whose balance has dropped below the original
          amount are reported as failed and left untouched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <Skeleton className="h-32 w-full" />}

        {!loading && batches.length === 0 && (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            No bulk grants yet.
          </p>
        )}

        {!loading && batches.length > 0 && (
          <ul className="space-y-2">
            {batches.map((b) => (
              <li
                key={b.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border p-3 text-sm"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{b.category}</Badge>
                    <span className="font-medium">
                      {b.amount.toLocaleString()} credits ×{" "}
                      {b.user_count.toLocaleString()} user
                      {b.user_count === 1 ? "" : "s"}
                    </span>
                    {b.failed > 0 && (
                      <Badge variant="secondary">{b.failed} failed</Badge>
                    )}
                    {b.reversed && (
                      <Badge variant="destructive">reversed</Badge>
                    )}
                  </div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {b.reason}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtBatchDate(b.created_at)} · batch{" "}
                    <code className="font-mono">{b.batch_id.slice(0, 8)}…</code>
                  </p>
                </div>
                <div className="shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={b.reversed || b.succeeded === 0}
                    onClick={() => setTarget(b)}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reverse batch
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {lastResult && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="default">{lastResult.succeeded} reversed</Badge>
              {lastResult.skipped > 0 && (
                <Badge variant="secondary">
                  {lastResult.skipped} already reversed
                </Badge>
              )}
              {lastResult.failed > 0 && (
                <Badge variant="destructive">{lastResult.failed} failed</Badge>
              )}
              <span className="text-muted-foreground">
                Batch <code className="font-mono">{lastResult.batchId}</code>
              </span>
            </div>
            <ul className="max-h-64 space-y-1 overflow-auto text-xs">
              {lastResult.rows.map((r) => (
                <li
                  key={r.original_grant_id}
                  className="flex items-start justify-between gap-2 border-b border-border/40 py-1 last:border-b-0"
                >
                  <span className="truncate font-mono">{r.user_id}</span>
                  {r.ok ? (
                    <span className="text-muted-foreground">
                      reversed · balance{" "}
                      {r.balance_after?.toLocaleString() ?? "?"}
                    </span>
                  ) : r.skipped === "already_reversed" ? (
                    <span className="text-muted-foreground">
                      already reversed
                    </span>
                  ) : (
                    <span className="text-destructive">{r.error}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <BulkReverseDialog
        target={target}
        busy={busy}
        onCancel={() => setTarget(null)}
        onConfirm={(reason) => void runReverse(reason)}
      />
    </Card>
  );
}
